import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { showSaveSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { itemCategoriesService } from '@/services/itemCategoriesService'
import type { ItemCategory, ItemCategoryFormSavePayload } from '@/api/types'
import type { SubCategoryLine } from '../types'
import { newSubCategoryLine } from '../types'

function subsFromApi(
  subs: Array<{ id: number; name?: string; nameEn?: string; nameAr?: string; isActive?: boolean }>
): SubCategoryLine[] {
  return subs.map((s) =>
    newSubCategoryLine({
      id: s.id || 0,
      nameEn: String(s.nameEn ?? s.name ?? ''),
      nameAr: String(s.nameAr ?? ''),
      isActive: s.isActive ?? true,
    })
  )
}

export const InventoryCategoryEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isEditMode = id !== undefined && id !== 'new'

  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [categories, setCategories] = useState<ItemCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSubs, setLoadingSubs] = useState(false)

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [categoryNameEn, setCategoryNameEn] = useState('')
  const [categoryNameAr, setCategoryNameAr] = useState('')
  const [categoryIsActive, setCategoryIsActive] = useState(true)
  const [subLines, setSubLines] = useState<SubCategoryLine[]>(() =>
    Array.from({ length: 3 }, () => newSubCategoryLine())
  )

  const categoryOptions = useMemo(() => {
    return [
      { id: 'add-new', name: t('inventoryCategories.addNewCategory') },
      ...categories.map((c) => ({ id: String(c.id), name: c.name })),
    ]
  }, [categories, t])

  const applyParentSelection = useCallback(
    async (value: string) => {
      setSelectedCategoryId(value)

      if (!value) {
        setCategoryNameEn('')
        setCategoryNameAr('')
        setCategoryIsActive(true)
        setSubLines(Array.from({ length: 3 }, () => newSubCategoryLine()))
        return
      }

      if (value === 'add-new') {
        setCategoryNameEn('')
        setCategoryNameAr('')
        setCategoryIsActive(true)
        setSubLines(Array.from({ length: 3 }, () => newSubCategoryLine()))
        return
      }

      const catId = Number(value)
      if (!catId || Number.isNaN(catId)) {
        setSubLines(Array.from({ length: 3 }, () => newSubCategoryLine()))
        return
      }

      const selected = categories.find((c) => c.id === catId)
      setCategoryNameEn(selected?.nameEn || selected?.name || '')
      setCategoryNameAr(selected?.nameAr || '')
      setCategoryIsActive(selected?.isActive ?? true)

      try {
        setLoadingSubs(true)
        const subs = await itemCategoriesService.getSubCategoriesByCategoryId(catId)
        const next = subs.length ? subsFromApi(subs) : []
        setSubLines(next.length ? next : Array.from({ length: 3 }, () => newSubCategoryLine()))
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('inventoryCategories.failedToLoadSubCategories'))
        setSubLines(Array.from({ length: 3 }, () => newSubCategoryLine()))
      } finally {
        setLoadingSubs(false)
      }
    },
    [categories, t]
  )

  const refreshSelectedCategory = useCallback(
    async (opts?: { trySelectByName?: string }) => {
      if (!branchId) return
      try {
        setLoadingCategories(true)
        const list = await itemCategoriesService.getAllItemCategories(branchId)
        setCategories(list)

        const byName = (opts?.trySelectByName || '').trim().toLowerCase()
        const nextId =
          byName
            ? list.find((c) => (c.name || '').trim().toLowerCase() === byName)?.id
            : undefined

        const targetId = nextId ?? (selectedCategoryId !== 'add-new' ? Number(selectedCategoryId) : undefined)
        if (targetId && !Number.isNaN(targetId)) {
          // reload subs from server so table refreshes after save
          setSelectedCategoryId(String(targetId))
          const selected = list.find((c) => c.id === targetId)
          setCategoryNameEn(selected?.nameEn || selected?.name || categoryNameEn)
          setCategoryNameAr(selected?.nameAr || categoryNameAr)
          setCategoryIsActive(selected?.isActive ?? categoryIsActive)
          setLoadingSubs(true)
          const subs = await itemCategoriesService.getSubCategoriesByCategoryId(targetId)
          const next = subs.length ? subsFromApi(subs) : []
          setSubLines(next.length ? next : Array.from({ length: 3 }, () => newSubCategoryLine()))
          setLoadingSubs(false)
        } else if (selectedCategoryId === 'add-new') {
          // Keep add-new clean (avoid leaking previous selected name into input after refresh)
          setCategoryNameEn('')
          setCategoryNameAr('')
          setCategoryIsActive(true)
          setSubLines(Array.from({ length: 3 }, () => newSubCategoryLine()))
        }
      } catch {
        // ignore refresh errors; user already saved successfully
      } finally {
        setLoadingCategories(false)
        setLoadingSubs(false)
      }
    },
    [branchId, categoryIsActive, categoryNameAr, categoryNameEn, selectedCategoryId]
  )

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoadingCategories(true)
        const list = await itemCategoriesService.getAllItemCategories(branchId)
        setCategories(list)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('inventoryCategories.failedToLoadCategories'))
        setCategories([])
      } finally {
        setLoadingCategories(false)
      }
    }
    void load()
  }, [branchId, t])

  useEffect(() => {
    if (!isEditMode) return
    if (!id) return
    // When editing via route param, auto-select category and load its subcategories
    void applyParentSelection(id)
  }, [isEditMode, id, applyParentSelection])

  const handleBack = () => navigate('/inventory/categories')

  const addSubRows = (n: number) => {
    setSubLines((prev) => [...prev, ...Array.from({ length: n }, () => newSubCategoryLine())])
  }

  const updateSubLine = (index: number, patch: Partial<Pick<SubCategoryLine, 'nameEn' | 'nameAr' | 'isActive'>>) => {
    setSubLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) {
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!selectedCategoryId) {
      toast.error(t('inventoryCategories.selectCategory'))
      return
    }
    if (!categoryNameEn.trim()) {
      toast.error(t('inventoryCategories.validationCategoryNameEn'))
      return
    }
    if (!categoryNameAr.trim()) {
      toast.error(t('inventoryCategories.validationCategoryNameAr'))
      return
    }
    const isAddNew = selectedCategoryId === 'add-new'
    const selectedIdNum = isAddNew ? 0 : Number(selectedCategoryId || 0)
    const action: ItemCategoryFormSavePayload['categories']['action'] = isAddNew ? 'save' : 'update'

    const cleanedSubs = subLines
      .filter(
        (l) =>
          (l.id > 0 && l.isDeleted) ||
          l.nameEn.trim().length > 0 ||
          l.nameAr.trim().length > 0
      )
      .map((l) => ({
        id: l.id > 0 ? l.id : 0,
        name: l.nameEn.trim(),
        nameAr: l.nameAr.trim(),
        isActive: Boolean(l.isActive),
        categoryId: isAddNew ? 0 : selectedIdNum,
        isDeleted: Boolean(l.isDeleted),
      }))
      .filter((l) => l.isDeleted || l.name.length > 0 || l.nameAr.length > 0)

    const payload: ItemCategoryFormSavePayload = {
      categories: {
        itemCategoryId: isAddNew ? 0 : selectedIdNum,
        itemCategoryNameEn: categoryNameEn.trim(),
        itemCategoryNameAr: categoryNameAr.trim(),
        isActive: Boolean(categoryIsActive),
        itemCategoryCode: '',
        action,
        branchId,
      },
      subCategories: cleanedSubs,
    }

    // Show exact payload structure in console (copy/paste friendly)
    // eslint-disable-next-line no-console
    console.log('ItemCategory FormSave payload:', payload)
    // eslint-disable-next-line no-console
    console.log('ItemCategory FormSave payload (JSON):\n' + JSON.stringify(payload, null, 2))

    try {
      await itemCategoriesService.formSave(payload)
      toast.dismiss()
      await showSaveSuccess(action === 'update')
      // Refresh the lines table after successful save
      await refreshSelectedCategory({ trySelectByName: isAddNew ? categoryNameEn : undefined })
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventoryCategories.saveFailed'))
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryCategories.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryCategories.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="overflow-hidden">
          <CardContent className="space-y-6 p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('inventoryCategories.sectionCategories')}
            </p>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
              <div className="space-y-2 md:max-w-[520px]">
                <Label>
                  {t('inventoryCategories.fieldCategory')} <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={selectedCategoryId}
                  onValueChange={(v) => applyParentSelection(v)}
                  disabled={loadingCategories}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('inventoryCategories.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent searchable>
                    {categoryOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!!selectedCategoryId && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
                <div className="space-y-2 md:max-w-[520px]">
                  <Label htmlFor="cat-name-en">
                    {t('inventoryCategories.fieldCategoryNameEn')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="cat-name-en"
                    value={categoryNameEn}
                    onChange={(e) => setCategoryNameEn(e.target.value)}
                    placeholder={t('inventoryCategories.categoryNameEnPlaceholder')}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2 md:max-w-[520px]">
                  <Label htmlFor="cat-name-ar">
                    {t('inventoryCategories.fieldCategoryNameAr')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="cat-name-ar"
                    value={categoryNameAr}
                    onChange={(e) => setCategoryNameAr(e.target.value)}
                    placeholder={t('inventoryCategories.categoryNameArPlaceholder')}
                    dir="rtl"
                    className="w-full text-right"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cat-active">{t('inventoryCategories.fieldIsActive')}</Label>
                  <div className="flex h-10 items-center gap-2 rounded-md border border-input bg-muted/30 px-3">
                    <Checkbox
                      id="cat-active"
                      checked={categoryIsActive}
                      onCheckedChange={(v) => setCategoryIsActive(Boolean(v))}
                    />
                    <span className="text-sm text-muted-foreground">{t('common.yes')}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b bg-gradient-to-r from-primary/10 to-secondary/10 px-4 py-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-semibold text-foreground">
                    {t('inventoryCategories.subCategoriesBar')}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => addSubRows(1)}>
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      {t('inventoryCategories.addRow')}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => addSubRows(3)}>
                      {t('inventoryCategories.add3Rows')}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => addSubRows(5)}>
                      {t('inventoryCategories.add5Rows')}
                    </Button>
                  </div>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 bg-primary/5 hover:bg-transparent">
                    <TableHead className="h-11 font-semibold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryCategories.columns.subNameEn')}
                    </TableHead>
                    <TableHead className="h-11 font-semibold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryCategories.columns.subNameAr')}
                    </TableHead>
                    <TableHead className="h-11 w-[120px] text-center font-semibold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryCategories.columns.subIsActive')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingSubs ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                        {t('common.loading')}
                      </TableCell>
                    </TableRow>
                  ) : subLines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                        {t('inventoryCategories.noSubRows')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    subLines.map((line, index) => (
                      <TableRow key={line.localId} className="border-b border-border">
                        <TableCell className="py-2">
                          <Input
                            value={line.nameEn}
                            onChange={(e) => updateSubLine(index, { nameEn: e.target.value })}
                            placeholder={t('inventoryCategories.subNameEnPlaceholder')}
                            className="border-input bg-muted/30"
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <Input
                            value={line.nameAr}
                            onChange={(e) => updateSubLine(index, { nameAr: e.target.value })}
                            placeholder={t('inventoryCategories.subNameArPlaceholder')}
                            dir="rtl"
                            className="border-input bg-muted/30 text-right"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={line.isActive}
                            onCheckedChange={(v) => updateSubLine(index, { isActive: Boolean(v) })}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end border-t pt-4">
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleBack}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit">
                  <Save className="mr-2 h-4 w-4" />
                  {isEditMode ? t('common.update') : t('inventoryCategories.save')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
